/**
 * Situation Validation Modal
 *
 * Modal pour valider, rejeter ou contester une situation de travaux
 * Inclut un canvas de signature pour la validation
 * Design System: Contexte DASHBOARD
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Pen,
  RotateCcw,
  FileCheck,
} from 'lucide-react';
import {
  useValidateSituation,
  useRejectSituation,
  useDisputeSituation,
} from '../hooks/useClientPortal';
import type { ClientSituation, ClientValidationAction } from '../types/client-portal.types';

interface SituationValidationModalProps {
  projectId: string;
  situation: ClientSituation;
  onClose: () => void;
  formatCurrency: (value: number) => string;
  formatDate: (date: string | null) => string;
}

type ActionStep = 'choice' | 'validate' | 'reject' | 'dispute';

export function SituationValidationModal({
  projectId,
  situation,
  onClose,
  formatCurrency,
  formatDate,
}: SituationValidationModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<ActionStep>('choice');
  const [reason, setReason] = useState('');
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);

  const validateMutation = useValidateSituation();
  const rejectMutation = useRejectSituation();
  const disputeMutation = useDisputeSituation();

  const isSubmitting = validateMutation.isPending || rejectMutation.isPending || disputeMutation.isPending;

  const handleValidate = async () => {
    if (!signatureDataUrl) return;

    validateMutation.mutate(
      {
        projectId,
        situationId: situation.id,
        input: {
          action: 'VALIDATED' as ClientValidationAction,
          signatureDataUrl,
        },
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  const handleReject = async () => {
    if (!reason.trim()) return;

    rejectMutation.mutate(
      {
        projectId,
        situationId: situation.id,
        reason: reason.trim(),
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  const handleDispute = async () => {
    if (!reason.trim()) return;

    disputeMutation.mutate(
      {
        projectId,
        situationId: situation.id,
        reason: reason.trim(),
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-white/40 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-50/50 to-white p-6 border-b border-white/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-teal-100 rounded-2xl">
                <FileCheck className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {t('common.clientPortal.situations.situationNumber', { number: situation.situationNumber })}
                </h2>
                <p className="text-gray-500">
                  {t('common.clientPortal.situations.period', { start: formatDate(situation.periodStart), end: formatDate(situation.periodEnd) })}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-2xl">
            <div>
              <p className="text-xs text-gray-500">{t('common.clientPortal.situations.amountHT')}</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(situation.periodAmountHT)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">{t('common.clientPortal.situations.tva')}</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(situation.periodAmountTVA)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">{t('common.clientPortal.situations.amountTTC')}</p>
              <p className="text-lg font-bold text-teal-600">
                {formatCurrency(situation.periodAmountTTC)}
              </p>
            </div>
          </div>

          {/* Step: Choice */}
          {step === 'choice' && (
            <div className="space-y-4">
              <p className="text-gray-600 text-center mb-6">
                {t('common.clientPortal.situations.whatToDo')}
              </p>
              <div className="grid gap-4">
                <button
                  onClick={() => setStep('validate')}
                  className="flex items-center gap-4 p-4 bg-green-50 hover:bg-green-100
                    border border-green-200 rounded-2xl transition-all duration-300 text-left"
                >
                  <div className="p-3 bg-green-100 rounded-xl">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-700">{t('common.clientPortal.situations.validateTitle')}</h3>
                    <p className="text-sm text-green-600">
                      {t('common.clientPortal.situations.validateDesc')}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setStep('dispute')}
                  className="flex items-center gap-4 p-4 bg-amber-50 hover:bg-amber-100
                    border border-amber-200 rounded-2xl transition-all duration-300 text-left"
                >
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-700">{t('common.clientPortal.situations.disputeTitle')}</h3>
                    <p className="text-sm text-amber-600">
                      {t('common.clientPortal.situations.disputeDesc')}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setStep('reject')}
                  className="flex items-center gap-4 p-4 bg-red-50 hover:bg-red-100
                    border border-red-200 rounded-2xl transition-all duration-300 text-left"
                >
                  <div className="p-3 bg-red-100 rounded-xl">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-700">{t('common.clientPortal.situations.rejectTitle')}</h3>
                    <p className="text-sm text-red-600">
                      {t('common.clientPortal.situations.rejectDesc')}
                    </p>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step: Validate with Signature */}
          {step === 'validate' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('common.clientPortal.situations.electronicSignature')}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {t('common.clientPortal.situations.signBelow')}
                </p>
                <SignatureCanvas
                  onSignatureChange={setSignatureDataUrl}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setStep('choice');
                    setSignatureDataUrl(null);
                  }}
                  className="flex-1 px-4 py-3 bg-white/50 hover:bg-white/70
                    text-gray-700 font-medium rounded-xl border border-gray-200
                    transition-all duration-300"
                >
                  {t('common.clientPortal.situations.back')}
                </button>
                <button
                  onClick={handleValidate}
                  disabled={!signatureDataUrl || isSubmitting}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700
                    text-white font-semibold rounded-xl
                    shadow-lg hover:shadow-xl transition-all duration-300
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('common.clientPortal.situations.validating') : t('common.clientPortal.situations.validateAndSign')}
                </button>
              </div>
            </div>
          )}

          {/* Step: Reject */}
          {step === 'reject' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('common.clientPortal.situations.rejectReasonTitle')}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {t('common.clientPortal.situations.rejectReasonDesc')}
                </p>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('common.clientPortal.situations.rejectPlaceholder')}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl
                    text-gray-900 placeholder-gray-400 resize-none
                    focus:ring-2 focus:ring-red-500/50 focus:border-transparent
                    transition-all duration-200"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setStep('choice');
                    setReason('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/50 hover:bg-white/70
                    text-gray-700 font-medium rounded-xl border border-gray-200
                    transition-all duration-300"
                >
                  {t('common.clientPortal.situations.back')}
                </button>
                <button
                  onClick={handleReject}
                  disabled={!reason.trim() || isSubmitting}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700
                    text-white font-semibold rounded-xl
                    shadow-lg hover:shadow-xl transition-all duration-300
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('common.clientPortal.situations.sending') : t('common.clientPortal.situations.confirmReject')}
                </button>
              </div>
            </div>
          )}

          {/* Step: Dispute */}
          {step === 'dispute' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('common.clientPortal.situations.disputeReasonTitle')}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {t('common.clientPortal.situations.disputeReasonDesc')}
                </p>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('common.clientPortal.situations.disputePlaceholder')}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl
                    text-gray-900 placeholder-gray-400 resize-none
                    focus:ring-2 focus:ring-amber-500/50 focus:border-transparent
                    transition-all duration-200"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setStep('choice');
                    setReason('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/50 hover:bg-white/70
                    text-gray-700 font-medium rounded-xl border border-gray-200
                    transition-all duration-300"
                >
                  {t('common.clientPortal.situations.back')}
                </button>
                <button
                  onClick={handleDispute}
                  disabled={!reason.trim() || isSubmitting}
                  className="flex-1 px-4 py-3 bg-amber-600 hover:bg-amber-700
                    text-white font-semibold rounded-xl
                    shadow-lg hover:shadow-xl transition-all duration-300
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('common.clientPortal.situations.sending') : t('common.clientPortal.situations.sendDispute')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Signature Canvas Component
function SignatureCanvas({
  onSignatureChange,
}: {
  onSignatureChange: (dataUrl: string | null) => void;
}) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getCoordinates = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const coords = getCoordinates(e);
    if (!coords) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  }, [getCoordinates]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  }, [isDrawing, getCoordinates]);

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);

    const canvas = canvasRef.current;
    if (!canvas) return;

    if (hasSignature) {
      const dataUrl = canvas.toDataURL('image/png');
      onSignatureChange(dataUrl);
    }
  }, [isDrawing, hasSignature, onSignatureChange]);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange(null);
  }, [onSignatureChange]);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#0D9488'; // teal-600
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  return (
    <div className="space-y-3">
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={500}
          height={200}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          className="w-full h-48 bg-white border-2 border-dashed border-gray-300 rounded-xl cursor-crosshair touch-none"
          style={{ touchAction: 'none' }}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 text-gray-400">
              <Pen className="w-5 h-5" />
              <span>{t('common.clientPortal.situations.signHere')}</span>
            </div>
          </div>
        )}
      </div>
      {hasSignature && (
        <button
          onClick={clearCanvas}
          className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800
            hover:bg-gray-100 rounded-lg transition-colors text-sm"
        >
          <RotateCcw className="w-4 h-4" />
          {t('common.clientPortal.situations.clearSignature')}
        </button>
      )}
    </div>
  );
}
